import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import { Heading, magma } from 'react-magma-dom';
import { DocsHeading } from '../../components/DocsHeading';
import SketchLogo from '../../images/logos/sketch-symbol.svg';
import InvisionLogo from '../../images/logos/invision_icon_pink.svg';
import AbstractLogo from '../../images/logos/abstract-logo.svg';
export const _frontmatter = {
  "pageTitle": "Design Guidelines - Getting Started",
  "title": "Getting Started",
  "order": 1
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const Paragraph = makeShortcode("Paragraph");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

<DocsHeading type="design" mdxType="DocsHeading">Getting Started</DocsHeading>
<Paragraph visualStyle="bodyLarge" mdxType="Paragraph">
  If you're new to designing with React Magma, then this is the right place to
  start. You'll find below everything you need to get going.
</Paragraph>
<hr></hr>
<h2>{`Designing with React Magma`}</h2>
<p>{`The React Magma team strives to provide detailed usage and style documentation. It's important to become familiar with this guidance when you are designing with our components. Consider starting with the foundations of `}<a parentName="p" {...{
        "href": "/version/2.6.2/design-intro/colors/"
      }}>{`color`}</a>{`, `}<a parentName="p" {...{
        "href": "/version/2.6.2/design-intro/typography/"
      }}>{`typography`}</a>{`, `}<a parentName="p" {...{
        "href": "/version/2.6.2/design-intro/spacing/"
      }}>{`spacing`}</a>{`, and `}<a parentName="p" {...{
        "href": "/version/2.6.2/design-intro/icons/"
      }}>{`icons`}</a>{`, and then continue with the components.`}</p>
<p>{`We also welcome contributions from people outside the React Magma team. This includes both designers within Cengage as well as outside. Join us over on our `}<a parentName="p" {...{
        "href": "https://github.com/cengage/react-magma"
      }}>{`Github`}</a>{` page to start contributing today.`}</p>
<hr></hr>
<h2>{`Resources & Tools`}</h2>
<div className="heading-leading-img">
  <Heading level={3} mdxType="Heading">
    <SketchLogo style={{
          width: '40px'
        }} mdxType="SketchLogo" />
    Sketch
  </Heading>
</div>
<p>{`Sketch is the primary application we use for designing products within Cengage. We maintain a comprehensive set of `}<a parentName="p" {...{
        "href": "https://www.dropbox.com/sh/kt8oaf7am443794/AAArcOLft67q5Fk90wcXSmida?dl=0"
      }}>{`React Magma Sketch libraries you can download`}</a>{` containing type styles, icons, and components. If you are a designer at Cengage, and you are a current Abstract user, then you'll find the libraries there instead.`}</p>
<p>{`To use the Sketch libraries you need the most recent version of `}<a parentName="p" {...{
        "href": "https://www.sketch.com/"
      }}>{`Sketch`}</a>{` installed.`}</p>
<div className="heading-leading-img">
  <Heading level={3} mdxType="Heading">
    <AbstractLogo style={{
          width: '40px'
        }} mdxType="AbstractLogo" />
    Abstract
  </Heading>
</div>
<p>{`We use Abstract to store all of our design files. Abstract provides version control and collaboration tools.
`}<a parentName="p" {...{
        "href": "https://www.abstract.com/"
      }}>{`Visit Abstract Website`}</a></p>
<div className="heading-leading-img">
  <Heading level={3} mdxType="Heading">
    <InvisionLogo style={{
          width: '40px'
        }} mdxType="InvisionLogo" />
    inVision
  </Heading>
</div>
<p>{`We use InVision to share our designs with stakeholders and create interactive prototypes.
`}<a parentName="p" {...{
        "href": "https://www.invisionapp.com/"
      }}>{`Visit InVision Website`}</a></p>
<hr></hr>
<h2>{`Guiding Principles`}</h2>
<p>{`Cengage exists to empower students and instructors with the audacity to realize their potential and the tenacity to achieve their goals.
React Magma helps us build tools that are relevant and easy to use, and create educational experiences that accelerate learning while putting our users at the center of everything we do. The following design principles guide every decision we make.`}</p>
<div className="principles-c anticipate-container">
  <h3>Anticipate Needs</h3>
  <p>
    Don't overburden me with options or choice. Proactively assume what I need
    and when, while equipping me with the ability to change it.
  </p>
  <blockquote>
    <p>
      &ldquo;I hate going through each student or each activity and changing
      things when I'm changing the same thing ... on 10, 20 or 30 of them. It is
      so time consuming.&rdquo;
    </p>
    <footer>&ndash; MindTap Instructor</footer>
  </blockquote>
</div>
<div className="principles-c encourage-container">
  <h3>Tolerate Mistakes</h3>
  <p>
    Mistakes are proof that I’m trying, but they should never impede my
    education experience. Preventing and helping me recover from a mistake
    should be as easy as it was to make it.
  </p>
  <blockquote>
    <p>
      &ldquo;...I feel like I am not learning with this software because I have
      no way of knowing where I made my mistakes.&rdquo;
    </p>
    <footer>&ndash; SAM Student</footer>
  </blockquote>
</div>
<div className="principles-c inspire-container">
  <h3>Inspire Confidence</h3>
  <p>
    Build confidence at every opportunity. Confidence that I can improve my
    understanding, confidence that the paragraph I just wrote won’t get lost and
    confidence that I won’t get lost.
  </p>
  <blockquote>
    <p>
      &ldquo;I’m calling in because all of my material on my account is
      gone.&rdquo;
    </p>
    <footer>&ndash; MindTap Instructor</footer>
  </blockquote>
</div>
<div className="principles-c cultivate-container">
  <h3>Cultivate Focus</h3>
  <p>
    Distractions are plentiful and my undivided attention is scarce. Any
    interference must be purposefully crafted to never override my education
    experience.
  </p>
  <blockquote>
    <p>
      &ldquo;... You also put up an online pop-up advertisement that would not
      go away for a solid week and prevented me from being able to complete the
      homework for that week at a reasonable pace.&rdquo;
    </p>
    <footer>&ndash; WebAssign Student</footer>
  </blockquote>
</div>
<div className="principles-c preserve-container">
  <h3>Preserve Simplicity</h3>
  <p>
    Be intentionally simple. Making common tasks fast, and easy for me to
    understand without explanation allows me to concentrate on my education
    experience.
  </p>
  <blockquote>
    <p>
      &ldquo;The organization was complicated and it was difficult to find
      assignments that were due...&rdquo;
    </p>
    <footer>&ndash; MindTap Student</footer>
  </blockquote>
</div>
<div className="principles-c discover-container">
  <h3>Encourage Mobility</h3>
  <p>
    I am not constrained by device or location and I expect the same of my
    education experience. Mobile empowers me to succeed, regardless of location,
    device or time.
  </p>
  <blockquote>
    <p>
      &ldquo;I shouldn't have to use my computer to take my tests or do my
      homework when I have a perfectly capable mobile device. I take online
      courses so that I can do my homework anywhere anytime.&rdquo;
    </p>
    <footer>&ndash; CNOW Student</footer>
  </blockquote>
</div>
<span className="horizontal-spacer"></span>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      